// The admin tour: one stop per area of the app, in the order work flows through a
// warehouse. Steps a person lacks permission for are skipped automatically.
import type { TourStep } from '../components/tour';

export const ADMIN_TOUR: TourStep[] = [
  {
    id: 'welcome',
    route: '/',
    title: 'Welcome to Zenarty WMS',
    body: (
      <>
        <p>
          Zenarty runs your warehouses: goods arrive on trucks, wait on shelves, and leave on
          orders. Every box is tracked, every client&apos;s goods are kept separate, and every
          change is recorded.
        </p>
        <p>This tour takes about two minutes. Use the arrow keys or the buttons; Esc closes it.</p>
      </>
    ),
  },
  {
    id: 'menu',
    route: '/',
    target: 'aside.sidebar nav',
    placement: 'right',
    title: 'The menu',
    body: (
      <p>
        Screens are grouped the way work flows: <strong>Inbound</strong> (goods arriving),{' '}
        <strong>Outbound</strong> (orders leaving), <strong>Inventory</strong>,{' '}
        <strong>Catalog</strong> (products and places), <strong>Setup</strong> and{' '}
        <strong>Governance</strong>. People only see the screens their role allows.
      </p>
    ),
  },
  {
    id: 'today',
    route: '/',
    target: '.content .grid-stats',
    placement: 'bottom',
    title: 'Today at a glance',
    body: (
      <p>
        The first row is today: orders due, orders at risk of missing their truck, fill rate, trucks
        at the dock and how fast goods get onto shelves. Click a tile to jump to the screen behind
        it. Numbers refresh every minute.
      </p>
    ),
  },
  {
    id: 'checklist',
    route: '/',
    target: '[data-tour="checklist"]',
    placement: 'left',
    title: 'Go-live checklist',
    body: (
      <p>
        The setup steps a new warehouse needs: sites, clients, office and floor staff, a scanner and
        two-step sign-in. Ticks appear as you complete them.
      </p>
    ),
  },
  {
    id: 'receipts',
    route: '/inbound/receipts',
    target: '.page-header .actions',
    placement: 'bottom',
    permission: 'inbound:read',
    title: 'Receipts: goods coming in',
    body: (
      <p>
        Every delivery the warehouse expects. Create one with <strong>New receipt</strong> or load
        many from a spreadsheet with <strong>Import CSV</strong>. Clients can also send them from
        their portal.
      </p>
    ),
  },
  {
    id: 'receipt-flow',
    route: '/inbound/receipts',
    target: '.content .tabs',
    placement: 'bottom',
    permission: 'inbound:read',
    title: 'From truck to shelf',
    body: (
      <>
        <p>Open a receipt to work it:</p>
        <ul>
          <li>Check the truck in at a dock door (the dock-to-stock clock starts).</li>
          <li>Scan barcodes to receive; damage needs a reason and photos.</li>
          <li>Close it: differences beyond the client&apos;s tolerance need approval.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'putaway',
    route: '/inbound/putaway',
    target: '.content .table-card',
    placement: 'top',
    permission: 'inbound:read',
    title: 'Putaway',
    body: (
      <p>
        For every received pallet the system suggests a shelf that fits: right temperature,
        hazmat-safe, the right client, enough room. Workers scan the shelf to confirm; going
        elsewhere needs a reason.
      </p>
    ),
  },
  {
    id: 'board',
    route: '/outbound/board',
    target: '.page-header',
    placement: 'bottom',
    permission: 'outbound:manage',
    title: 'Order board',
    body: (
      <p>
        Every order due in the next two days, grouped by the time its truck leaves. Late orders turn
        red and risky ones amber, with the reason, so the shift lead knows what to push first.
      </p>
    ),
  },
  {
    id: 'orders',
    route: '/outbound/orders',
    target: '.page-header .actions',
    placement: 'bottom',
    permission: 'outbound:read',
    title: 'Orders',
    body: (
      <p>
        Orders arrive here from the API, CSV or the client portal. New orders are checked and stock
        is reserved straight away: soonest expiry first, and never a lot too short-dated for the
        retailer. <strong>Release ready orders</strong> sends them to the floor.
      </p>
    ),
  },
  {
    id: 'order-tabs',
    route: '/outbound/orders',
    target: '.content .tabs',
    placement: 'bottom',
    permission: 'outbound:read',
    title: 'Follow every order',
    body: (
      <p>
        Tabs follow an order&apos;s life: open, allocated, released, picking, picked, packed,
        shipped. <strong>Exceptions</strong> collects anything on hold, short of stock or past its
        cut-off.
      </p>
    ),
  },
  {
    id: 'waves',
    route: '/outbound/waves',
    target: '.page-header .actions',
    placement: 'bottom',
    permission: 'outbound:manage',
    title: 'Waves',
    body: (
      <p>
        Group orders and release them together: by carrier, retailer, client or ship date, picked
        one by one, in a batch, on a cart with a tote per order, or zone by zone.
      </p>
    ),
  },
  {
    id: 'picking',
    route: '/outbound/picking',
    target: '.content .table-card',
    placement: 'top',
    permission: 'outbound:pick',
    title: 'Picking',
    body: (
      <p>
        Pick lists in walking order. If a picker finds fewer items than expected, the system orders
        a recount, refills the shelf from reserve and finds the missing units elsewhere,
        automatically.
      </p>
    ),
  },
  {
    id: 'packing',
    route: '/outbound/packing',
    target: '.content .card',
    placement: 'bottom',
    permission: 'outbound:pack',
    title: 'Packing station',
    body: (
      <p>
        Scan an order, open a carton, scan each item in (wrong items are refused), weigh and close.
        Every carton and pallet gets a standard shipping label. Ship confirm then tells the client
        and records the charges.
      </p>
    ),
  },
  {
    id: 'stock',
    route: '/inventory',
    target: '.content .grid-stats',
    placement: 'bottom',
    permission: 'inventory:read',
    title: 'Stock',
    body: (
      <p>
        Exactly what is where, by client, item, location, lot and expiry. Every number comes from a
        permanent movement ledger, so any figure can be traced back to the scans that made it.
      </p>
    ),
  },
  {
    id: 'inventory-tools',
    route: '/inventory',
    target: 'aside.sidebar a[href="/inventory/counts"]',
    placement: 'right',
    permission: 'inventory:read',
    title: 'Counts, holds and recalls',
    body: (
      <p>
        <strong>Cycle counts</strong> check a few shelves a day (fast movers more often).{' '}
        <strong>Holds &amp; recalls</strong> freeze a lot everywhere in one step.{' '}
        <strong>Lot trace</strong> shows where a lot came from and where it went.
      </p>
    ),
  },
  {
    id: 'catalog',
    route: '/items',
    target: '.page-header .actions',
    placement: 'bottom',
    permission: 'item:read',
    title: 'Catalog: items',
    body: (
      <p>
        Every product each client stores, with its barcodes, case and pallet sizes, temperature and
        whether it needs lot, expiry or serial numbers. Import a whole catalogue from CSV.
      </p>
    ),
  },
  {
    id: 'locations',
    route: '/locations',
    target: '.page-header',
    placement: 'bottom',
    permission: 'location:read',
    title: 'Locations',
    body: (
      <p>
        The map of each warehouse: every shelf, its size and rules. <strong>Generate</strong> builds
        a whole rack layout in one go, numbered in walking order.
      </p>
    ),
  },
  {
    id: 'people',
    route: '/users',
    target: '.page-header .actions',
    placement: 'bottom',
    permission: 'user:read',
    title: 'Users & access',
    body: (
      <p>
        Add office staff, floor staff (badge and PIN for scanners) and client portal users. Roles
        decide what someone can do; access decides which sites and clients they see.
      </p>
    ),
  },
  {
    id: 'setup',
    route: '/clients',
    target: 'aside.sidebar a[href="/clients"]',
    placement: 'right',
    permission: 'client:read',
    title: 'Setup',
    body: (
      <p>
        <strong>Sites</strong> and <strong>Clients</strong> (with their receiving tolerances,
        backorder rules and cut-off), <strong>Scanners</strong>, <strong>Putaway rules</strong> and{' '}
        <strong>Configuration</strong> (stock statuses and reason codes) live here.
      </p>
    ),
  },
  {
    id: 'audit',
    route: '/audit',
    target: '.page-header',
    placement: 'bottom',
    permission: 'audit:read',
    title: 'Audit log',
    body: (
      <p>
        A permanent diary of who did what, when and from where, with before-and-after values. Nobody
        can edit or delete it.
      </p>
    ),
  },
  {
    id: 'you',
    route: '/',
    target: '.topbar .user-chip',
    placement: 'bottom',
    title: 'Your menu',
    body: (
      <p>
        Change your password, sign out, or <strong>Take the tour</strong> again from here. The
        button beside it switches between light, dark and your computer&apos;s theme.
      </p>
    ),
  },
  {
    id: 'done',
    route: '/',
    title: "You're ready",
    body: (
      <>
        <p>A good first run-through with the demo data:</p>
        <ul>
          <li>Inbound → Receipts: open one that is &quot;At dock&quot; and receive it.</li>
          <li>Outbound → Orders: release an allocated order, then pick and pack it.</li>
          <li>Inventory → Lot trace: follow a lot from truck to customer.</li>
        </ul>
      </>
    ),
  },
];
